import { getAuthenticatedHttpClient, getHttpClient } from '@edx/frontend-platform/auth';

import { getCookie } from './cookies';

type Method = RequestInit['method'];

type HttpClient = {
  request<T = unknown>(config: {
    url: string;
    method?: Method;
    data?: unknown;
    params?: Record<string, unknown>;
    headers?: Record<string, string>;
    withCredentials?: boolean;
  }): Promise<{ data: T }>;
};

const CSRF_HEADER = 'X-CSRFToken';
const SAFE_METHODS = new Set<Method>(['GET', 'HEAD', 'OPTIONS', 'TRACE']);

const needsCsrf = (method?: Method) => (
  !SAFE_METHODS.has(method ?? 'GET')
);

const getApiClient = (isAuth: boolean): HttpClient => (
  (isAuth ? getAuthenticatedHttpClient() : getHttpClient())
);

/**
 * Custom API error with optional status and payload.
 */
class ApiError<P = unknown> extends Error {
  status?: number;

  payload?: P;

  cause?: unknown;

  constructor(message: string, opts?: { status?: number; payload?: P; cause?: unknown }) {
    super(message);
    this.name = 'ApiError';
    this.status = opts?.status;
    this.payload = opts?.payload;
    this.cause = opts?.cause;
  }
}

/**
 * Type guard to detect axios-like error structure.
 */
const isAxiosLikeError = (e: unknown): e is { response?: { status?: number; data?: unknown } } => (
  typeof e === 'object' && e !== null && 'response' in e
);

/**
 * Universal request. The client chooses and supplies the CSRF for the anonymized user when using insecure methods.
 */
export async function apiRequest<T = unknown>(opts: {
  isAuth: boolean;
  url: string;
  method?: Method;
  data?: unknown;
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
  contentType?: string;
}): Promise<T> {
  const {
    isAuth, url, method = 'GET', data, params, headers, contentType,
  } = opts;

  const client = getApiClient(isAuth);

  const finalHeaders: Record<string, string> = {
    ...(contentType ? { 'Content-Type': contentType } : {}),
    ...(headers ?? {}),
  };

  if (!isAuth && needsCsrf(method)) {
    const token = getCookie('csrftoken');
    if (token) {
      finalHeaders[CSRF_HEADER] = token;
    }
  }

  try {
    const res = await client.request<T>({
      url,
      method,
      data,
      params,
      headers: finalHeaders,
      withCredentials: true,
    });
    return res.data;
  } catch (err: any) {
    const message = (err as Error)?.message ?? 'Request failed';
    const status = isAxiosLikeError(err) ? err.response?.status : undefined;
    const payload = isAxiosLikeError(err) ? err.response?.data : undefined;

    throw new ApiError(message, { status, payload, cause: err });
  }
}
